import { useSelector, useDispatch } from "react-redux";
import {
  setCodigoBarra,
  setArticulo,
  setStockTeorico,
  setStockReal,
  setMsgError,
  setArticulosDisponibles,
  setArticuloActual,
  avanzarArticuloAlAzar,
  resetFormulario,
  openCancelarDialog,
  closeCancelarDialog,
  openProximoDialog,
  closeProximoDialog,
  openFinalizarDialog,
  closeFinalizarDialog,
  agregarArticuloControlado,
  resetArticulosControlados,
  setMensajeComprobante,
  resetMensajeComprobante,
  resetTodo,
  guardarYResetear,
  marcarInexistente,
  selectCodigoBarra,
  selectArticulo,
  selectStockTeorico,
  selectStockReal,
  selectMsgError,
  selectArticulosDisponibles,
  selectArticuloActual,
  selectArticulosControlados,
  selectMensajeComprobante,
  selectCancelarDialogOpen,
  selectProximoDialogOpen,
  selectFinalizarDialogOpen,
  selectFormValidation,
  selectControlStats,
  selectDialogState,
  selectTotalArticulosControlados,
  selectHayDiferenciasStock,
  selectArticulosConDiferencias,
  selectArticuloPorCodigo,
} from "../store/stockControl/stockControlSlice";

/**
 * @typedef {Object} ArticuloControlado
 * @property {number} idArticulo
 * @property {string} descripcion
 * @property {number} stockReal
 * @property {number|null} stockTeorico
 * @property {string} [codigo]
 * @property {string} [codigoBarra]
 * @property {string} [ubicacion]
 */

/**
 * @typedef {Object} ArticuloBasico
 * @property {number} idArticulo
 * @property {string} descripcion
 */

/**
 * @typedef {Object} ArticuloIMAPorId
 * @property {number} idArticulo
 * @property {string} descripcion
 * @property {number|null} stockTeorico
 * @property {string} [codigo]
 * @property {string} [codigoBarra]
 */

/**
 * Hook principal para el control de stock
 * Mantiene la misma API que el store de Zustand
 */
export const useStockControlStore = () => {
  const dispatch = useDispatch();

  // Selectores
  const codigoBarra = useSelector(selectCodigoBarra);
  const articulo = useSelector(selectArticulo);
  const stockTeorico = useSelector(selectStockTeorico);
  const stockReal = useSelector(selectStockReal);
  const msgError = useSelector(selectMsgError);
  const articulosDisponibles = useSelector(selectArticulosDisponibles);
  const articuloActual = useSelector(selectArticuloActual);
  const articulosControlados = useSelector(selectArticulosControlados);
  const mensajeComprobante = useSelector(selectMensajeComprobante);
  const cancelarDialogOpen = useSelector(selectCancelarDialogOpen);
  const proximoDialogOpen = useSelector(selectProximoDialogOpen);
  const finalizarDialogOpen = useSelector(selectFinalizarDialogOpen);

  return {
    // ===== Estados =====
    codigoBarra,
    articulo,
    stockTeorico,
    stockReal,
    msgError,
    articulosDisponibles,
    articuloActual,
    articulosControlados,
    mensajeComprobante,
    cancelarDialogOpen,
    proximoDialogOpen,
    finalizarDialogOpen,

    // ===== Acciones =====
    setCodigoBarra: codigo => dispatch(setCodigoBarra(codigo)),
    setArticulo: articulo => dispatch(setArticulo(articulo)),
    setStockTeorico: stock => dispatch(setStockTeorico(stock)),
    setStockReal: stock => dispatch(setStockReal(stock)),
    setMsgError: msg => dispatch(setMsgError(msg)),
    setArticulosDisponibles: lista => dispatch(setArticulosDisponibles(lista)),
    setArticuloActual: articulo => dispatch(setArticuloActual(articulo)),
    avanzarArticuloAlAzar: () => dispatch(avanzarArticuloAlAzar()),
    resetFormulario: () => dispatch(resetFormulario()),
    openCancelarDialog: () => dispatch(openCancelarDialog()),
    closeCancelarDialog: () => dispatch(closeCancelarDialog()),
    openProximoDialog: () => dispatch(openProximoDialog()),
    closeProximoDialog: () => dispatch(closeProximoDialog()),
    openFinalizarDialog: () => dispatch(openFinalizarDialog()),
    closeFinalizarDialog: () => dispatch(closeFinalizarDialog()),
    agregarArticuloControlado: item => dispatch(agregarArticuloControlado(item)),
    resetArticulosControlados: () => dispatch(resetArticulosControlados()),
    setMensajeComprobante: msg => dispatch(setMensajeComprobante(msg)),
    resetMensajeComprobante: () => dispatch(resetMensajeComprobante()),
    resetTodo: () => dispatch(resetTodo()),

    // ===== Métodos auxiliares =====
    getTotalArticulosControlados: () => {
      return articulosControlados.length;
    },
    hayDiferenciasStock: () => {
      return articulosControlados.some(articulo => articulo.stockReal !== articulo.stockTeorico);
    },
    getArticulosConDiferencias: () => {
      return articulosControlados.filter(articulo => articulo.stockReal !== articulo.stockTeorico);
    },
  };
};

/**
 * Hook para obtener el estado de validación del formulario
 */
export const useFormValidation = () => {
  return useSelector(selectFormValidation);
};

/**
 * Hook para obtener estadísticas del control
 */
export const useControlStats = () => {
  return useSelector(selectControlStats);
};

/**
 * Hook para obtener el estado de los diálogos
 */
export const useDialogState = () => {
  return useSelector(selectDialogState);
};

/**
 * Hook para acciones complejas de manejo de artículos
 */
export const useArticuloActions = () => {
  const dispatch = useDispatch();
  const articulosDisponibles = useSelector(selectArticulosDisponibles);

  return {
    /**
     * Guarda un artículo controlado y resetea el formulario
     * @param {ArticuloControlado} articulo
     */
    guardarYResetear: articulo => {
      dispatch(guardarYResetear(articulo));
    },

    /**
     * Marca un artículo como inexistente (stock = 0)
     * @param {ArticuloControlado} articulo
     */
    marcarInexistente: articulo => {
      dispatch(marcarInexistente(articulo));
    },

    /**
     * Busca un artículo por código
     * @param {string} codigo
     * @returns {ArticuloBasico|null}
     */
    buscarPorCodigo: codigo => {
      return articulosDisponibles.find(a => a.codigo === codigo || a.codigoBarra === codigo) || null;
    },
  };
};

// ===== Hooks para selectores específicos =====
export const useCodigoBarra = () => useSelector(selectCodigoBarra);
export const useArticulo = () => useSelector(selectArticulo);
export const useStockTeorico = () => useSelector(selectStockTeorico);
export const useStockReal = () => useSelector(selectStockReal);
export const useMsgError = () => useSelector(selectMsgError);
export const useArticulosDisponibles = () => useSelector(selectArticulosDisponibles);
export const useArticuloActual = () => useSelector(selectArticuloActual);
export const useArticulosControlados = () => useSelector(selectArticulosControlados);
export const useMensajeComprobante = () => useSelector(selectMensajeComprobante);
export const useTotalArticulosControlados = () => useSelector(selectTotalArticulosControlados);
export const useHayDiferenciasStock = () => useSelector(selectHayDiferenciasStock);
export const useArticulosConDiferencias = () => useSelector(selectArticulosConDiferencias);

// ===== Hook para acciones específicas =====
export const useStockControlActions = () => {
  const dispatch = useDispatch();

  return {
    setCodigoBarra: codigo => dispatch(setCodigoBarra(codigo)),
    setArticulo: articulo => dispatch(setArticulo(articulo)),
    setStockTeorico: stock => dispatch(setStockTeorico(stock)),
    setStockReal: stock => dispatch(setStockReal(stock)),
    setMsgError: msg => dispatch(setMsgError(msg)),
    setArticulosDisponibles: lista => dispatch(setArticulosDisponibles(lista)),
    setArticuloActual: articulo => dispatch(setArticuloActual(articulo)),
    avanzarArticuloAlAzar: () => dispatch(avanzarArticuloAlAzar()),
    resetFormulario: () => dispatch(resetFormulario()),
    openCancelarDialog: () => dispatch(openCancelarDialog()),
    closeCancelarDialog: () => dispatch(closeCancelarDialog()),
    openProximoDialog: () => dispatch(openProximoDialog()),
    closeProximoDialog: () => dispatch(closeProximoDialog()),
    openFinalizarDialog: () => dispatch(openFinalizarDialog()),
    closeFinalizarDialog: () => dispatch(closeFinalizarDialog()),
    agregarArticuloControlado: item => dispatch(agregarArticuloControlado(item)),
    resetArticulosControlados: () => dispatch(resetArticulosControlados()),
    setMensajeComprobante: msg => dispatch(setMensajeComprobante(msg)),
    resetMensajeComprobante: () => dispatch(resetMensajeComprobante()),
    resetTodo: () => dispatch(resetTodo()),
    guardarYResetear: articulo => dispatch(guardarYResetear(articulo)),
    marcarInexistente: articulo => dispatch(marcarInexistente(articulo)),
  };
};

export default useStockControlStore;
